import { routes } from '@platform/core/lib/routes';
import { catalogLabel, groupModules, moduleNav, navGroups, systemItem, systemModules } from '@platform/navigation';
import type { IModuleCard } from './IModuleCard';
import type { IModuleSection } from './IModuleSection';
import type { IModuleSectionsInput } from './IModuleSectionsInput';
import { appRoutes } from './appRoutes';
import { moduleCount } from './moduleCount';

/** Плитка модуля або ярлика «модуль/тип запису» з каталогу; невстановлений модуль чи тип — null. */
function card(input: IModuleSectionsInput, entry: string): IModuleCard | null {
  const [key, rtKey] = entry.split('/') as [string, string | undefined];
  const summary = input.catalog?.modules.find((m) => m.key === key && m.installed);
  const rt = rtKey ? summary?.recordTypes.find((r) => r.key === rtKey) : undefined;
  if (!summary || (rtKey && !rt)) return null;
  const nav = moduleNav(key);
  return {
    key: entry, name: input.label(rt?.name ?? summary.name), description: input.describe?.(key) || input.label(summary.tagline), icon: nav.icon, tone: nav.tone,
    count: moduleCount(summary, rt?.key), to: rt ? routes.list(key, rt.key) : routes.module(key), pinned: input.pinned.includes(key),
  };
}

/** Сторінки системних модулів: Головна, Адміністрування, Налаштування (прототип v4: page home / adm / cust). */
const SYSTEM_PAGES: Record<string, () => string> = {
  home: () => routes.home(), administration: () => appRoutes.admin(), customization: () => appRoutes.customize(),
};

/** Збіг із запитом «Шукати модулі»: назва, опис, префікс і назва групи (як navQ прототипу v4). */
const matches = (c: IModuleCard, q: string, extra: string): boolean => !q || `${c.name} ${c.description} ${c.key} ${extra}`.toLowerCase().includes(q);

/**
 * Розділи сторінки «Усі модулі» (прототип v4): групи верхнього меню в порядку моделі навігації (лише встановлені модулі в порядку простору, що збігаються з
 * запитом), «Закріплені» для фільтра pinned і «Система» (Головна, Адміністрування, Налаштування) — для всіх і для фільтра system.
 * Порожні розділи відкидаються. Чиста функція.
 */
export function moduleSections(input: IModuleSectionsInput): IModuleSection[] {
  const q = input.q.trim().toLowerCase();
  const prefixOf = (key: string) => input.catalog?.modules.find((m) => m.key === key.split('/')[0])?.prefix ?? '';
  const pick = (entries: string[], groupName: string) =>
    entries.map((e) => card(input, e)).filter((c): c is IModuleCard => !!c && matches(c, q, `${groupName} ${prefixOf(c.key)}`));
  const sections: IModuleSection[] = [];
  if (input.group === 'pinned') sections.push({ key: 'pinned', label: input.pinnedLabel, cards: pick(input.pinned, input.pinnedLabel) });
  for (const g of navGroups()) {
    if (input.group && input.group !== g.key) continue;
    const name = input.label(catalogLabel(g));
    sections.push({ key: g.key, label: name, cards: pick(groupModules(g, input.parliament ?? true), name) });
  }
  if (!input.group || input.group === 'system') {
    const cards = systemModules().map((key): IModuleCard | null => {
      const item = systemItem(key);
      return item ? { key, name: input.label(item.label), description: input.label(item.description), icon: item.icon, tone: item.tone,
        to: SYSTEM_PAGES[key]?.(), onOpen: SYSTEM_PAGES[key] ? undefined : () => input.onSystem(key) } : null;
    }).filter((c): c is IModuleCard => !!c && matches(c, q, input.systemLabel));
    sections.push({ key: 'system', label: input.systemLabel, cards });
  }
  return sections.filter((s) => s.cards.length);
}
